import React, { useEffect, useState } from "react";
import { Popover, Tooltip } from "antd";
import { NavLink } from "react-router-dom";
import { SettingOutlined } from "@ant-design/icons";
import { getResource as rbacGetResource } from "../../util/getResourcePermission";
import config from "../../permission/config.json";
import "./SettingsDropdown.css";
import { getRoles } from "../../util/getRoles";

const SettingsDropdown = () => {
  const [visible, setVisible] = useState(false);
  const companySlug = localStorage.getItem("companyDomain");

  // ── Plan-aware permission helpers ──────────────────────────────────────────
  const sidebarModuleAccess = (() => {
    try {
      const raw = JSON.parse(localStorage.getItem("sidebarModuleAccess") || "[]");
      return new Set(Array.isArray(raw) ? raw : []);
    } catch { return new Set(); }
  })();

  const normalizeSlug = (v = "") =>
    String(v).trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");

  // Resources from the plan catalog that appear in the settings dropdown.
  // Settings-specific resources (SETTINGS, DEPARTMENTS, SHIFT, etc.) are NOT in the
  // plan catalog, so they have no entry here and fall through to RBAC-only.
  const SETTINGS_PLAN_MAP = {
    // activity_logs is in the plan catalog (Top Level) but is checked via
    // isActivityLogsPlanEnabled() below since it uses getRoles() not getResource().
    // Form Builder inherits each module's plan access (same slugs the sidebar uses).
    onboarding_initiation: ["doc_master", "onboarding_master", "onboarding_initiation"],
    candidate_approval: ["candidate_approval", "candidate_list"],
    employee_master: ["employee_master"],
  };

  // Step 1: SuperAdmin plan check (only for plan-catalog resources).
  // Step 2: Internal RBAC check.
  // Settings resources not in SETTINGS_PLAN_MAP skip the plan gate entirely.
  const getResource = (name, types = []) => {
    if (sidebarModuleAccess.size > 0) {
      const key = normalizeSlug(name);
      const planSlugs = SETTINGS_PLAN_MAP[key];
      if (planSlugs) {
        if (!planSlugs.some((slug) => sidebarModuleAccess.has(slug))) return false;
      }
    }
    return rbacGetResource(name, types);
  };

  // Plan gate - pass null slug to skip plan check (RBAC-only item).
  const isPlanEnabled = (slug) =>
    !slug || sidebarModuleAccess.size === 0 || sidebarModuleAccess.has(slug);

  // Combined check: RBAC resource permission + SuperAdmin plan module access.
  // Pass null for resource to skip RBAC; null for slug to skip plan gate.
  const canShowSetting = (resource, slug) => {
    const hasRbac = resource ? rbacGetResource(resource, ["read"]) : true;
    return hasRbac && isPlanEnabled(slug);
  };
  // ──────────────────────────────────────────────────────────────────────────

  const handleVisibleChange = (newVisible) => {
    setVisible(newVisible);
    // Tell the profile menu to close - both open from the topbar and overlap.
    if (newVisible) window.dispatchEvent(new CustomEvent("topbar-popover-open", { detail: "settings" }));
  };

  useEffect(() => {
    const closeOnProfileOpen = (e) => {
      if (e.detail === "profile") setVisible(false);
    };
    window.addEventListener("topbar-popover-open", closeOnProfileOpen);
    return () => window.removeEventListener("topbar-popover-open", closeOnProfileOpen);
  }, []);

  const handleLinkClick = () => {
    setVisible(false);
  };
  const hasSettingsAccess = getResource(config.RESOURCES.SETTINGS, ["read"]);

  const settingsContent = (
    <div className="settings-dropdown-scroll">
    <div className="settings-dropdown-wrapper">
      <div className="settings-section">
        <div className="settings-section-title">General</div>
        <div className="settings-links">
          {hasSettingsAccess && (
            <NavLink
              to={`/${companySlug}/admin/company-registartion`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Company Profile
            </NavLink>
          )}
          {hasSettingsAccess && (
            <NavLink
              to={`/${companySlug}/admin/settings`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              System Settings
            </NavLink>
          )}
          {canShowSetting(config.RESOURCES.SETTINGS, "zoho_payroll_integration") && (
            <NavLink to={`/${companySlug}/admin/zoho-payroll-integration`} className="settings-link" onClick={handleLinkClick}>
              Zoho Payroll Integration
            </NavLink>
          )}
          {canShowSetting(config.RESOURCES.POLICIES, "organization_policy") && (
            <NavLink to={`/${companySlug}/policies`} className="settings-link" onClick={handleLinkClick}>
              Policy Setup
            </NavLink>
          )}
          {hasSettingsAccess && (
            <NavLink
              to={`/${companySlug}/permission-manager`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Permission Management
            </NavLink>
          )}
          {hasSettingsAccess && (
            <NavLink
              to={`/${companySlug}/admin-email-master`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Email Master
            </NavLink>
          )}
          {canShowSetting(config.RESOURCES.SETTINGS, "onboarding_initiation") && (
            <NavLink to={`/${companySlug}/admin-document-master`} className="settings-link" onClick={handleLinkClick}>
              Document Master
            </NavLink>
          )}
          {hasSettingsAccess && (
            <NavLink
              to={`/${companySlug}/CompanyCronList`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Cron Master
            </NavLink>
          )}
          {getResource(config.RESOURCES.MEETING_ROOM, ["read"]) && (
            <NavLink
              to={`/${companySlug}/meeting-rooms`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Meeting Room Master
            </NavLink>
          )}
          {(canShowSetting(config.RESOURCES.LEAVE_MASTER, "admin_leave_management") ||
            canShowSetting(config.RESOURCES.MANAGE_LEAVE_BALANCE, "admin_leave_management")) && (
              <NavLink to={`/${companySlug}/admin-leave-configuration`} className="settings-link" onClick={handleLinkClick}>
                Leave Settings
              </NavLink>
            )}
          {getResource(config.RESOURCES.HOLIDAYS, ["read"]) && (
            <NavLink
              to={`/${companySlug}/holidays`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Holiday Calendar
            </NavLink>
          )}
          {getResource(config.RESOURCES.ACHIEVEMENTS, ["read"]) && (
            <NavLink
              to={`/${companySlug}/achivements`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Achievements
            </NavLink>
          )}
          {getRoles(["Admin"]) && isPlanEnabled("activity_logs") && (
            <NavLink to={`/${companySlug}/activity-logs`} className="settings-link" onClick={handleLinkClick}>
              Activity Logs
            </NavLink>
          )}
          {hasSettingsAccess && (
            <NavLink
              to={`/${companySlug}/frs-management`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Face Identity Vault
            </NavLink>
          )}
        </div>
      </div>

      <div className="settings-section">
        <div className="settings-section-title">Organization Structure</div>
        <div className="settings-links">
          {(getResource(config.RESOURCES.ON_BOARDING_INITIATION, ["read"]) ||
            getResource(config.RESOURCES.CANDIDATE_APPROVAL, ["read"]) ||
            getResource(config.RESOURCES.EMPLOYEE_MASTER, ["read"])) && (
            <NavLink
              to={`/${companySlug}/form-builder`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Form Builder
            </NavLink>
          )}
          {getResource(config.RESOURCES.BRANCH_OPTIONS, ["read"]) && (
            <NavLink
              to={`/${companySlug}/admin-branch`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Branch
            </NavLink>
          )}
          {getResource(config.RESOURCES.BUSINESS_UNIT, ["read"]) && (
            <NavLink
              to={`/${companySlug}/admin-businessunit`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Business Unit
            </NavLink>
          )}
          {getResource(config.RESOURCES.DEPARTMENTS, ["read"]) && (
            <NavLink
              to={`/${companySlug}/admin-department`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Department
            </NavLink>
          )}
          {getResource(config.RESOURCES.SUBDEPARTMENT, ["read"]) && (
            <NavLink
              to={`/${companySlug}/admin-subdepartment`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Sub-Department
            </NavLink>
          )}
          {getResource(config.RESOURCES.DESIGNATION, ["read"]) && (
            <NavLink
              to={`/${companySlug}/admin-designation`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Designation
            </NavLink>
          )}
          {getResource(config.RESOURCES.EMPLOYEMENT_TYPE, ["read"]) && (
            <NavLink
              to={`/${companySlug}/admin-employment-type`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Employment Type
            </NavLink>
          )}
          {getResource(config.RESOURCES.FUNCTION_ROLE, ["read"]) && (
            <NavLink
              to={`/${companySlug}/admin-role`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Function Role
            </NavLink>
          )}
          {getResource(config.RESOURCES.EMPLOYEE_LEVEL, ["read"]) && (
            <NavLink
              to={`/${companySlug}/admin-employee-level`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Employee Level
            </NavLink>
          )}
          {getResource(config.RESOURCES.SHIFT, ["read"]) && (
            <>
              <NavLink
                to={`/${companySlug}/admin-shift`}
                className="settings-link"
                onClick={handleLinkClick}
              >
                Work Week & Shifts
              </NavLink>
              <NavLink
                to={`/${companySlug}/admin-roster`}
                className="settings-link"
                onClick={handleLinkClick}
              >
                Roster
              </NavLink>
            </>
          )}
          {/* Moved to Engage > Announcements > Categories tab (mirrors LMS's
              Courses/Categories pattern) — kept discoverable there instead
              of buried in Settings, per Kartik's request. Route/component
              still exist for any deep link to the old URL. */}

          {getResource(config.RESOURCES.SETTINGS, ["read"]) && (
            <NavLink
              to={`/${companySlug}/admin-attendance-settings`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              General Settings
            </NavLink>
          )}
          {getResource(config.RESOURCES.SETTINGS, ["read"]) && (
            <NavLink
              to={`/${companySlug}/admin-notice-period-policies`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Notice Period Policies
            </NavLink>
          )}
          {getResource(config.RESOURCES.SETTINGS, ["read"]) && (
            <NavLink
              to={`/${companySlug}/action-hub-settings`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Action Hub Settings
            </NavLink>
          )}
          {canShowSetting(config.RESOURCES.APPROVAL_WORKFLOW_CONFIG, "approval_workflow") && (
            <NavLink
              to={`/${companySlug}/admin/approval-workflow`}
              className="settings-link"
              onClick={handleLinkClick}
            >
              Approval Workflow
            </NavLink>
          )}
        </div>
      </div>
    </div>
    </div>
  );

  return (
    <Popover
      content={settingsContent}
      trigger="hover"
      placement="bottomRight"
      visible={visible}
      onVisibleChange={handleVisibleChange}
      overlayClassName="settings-dropdown-popover"
      getPopupContainer={(trigger) => trigger.parentElement}
    >
      <span className="settings-trigger">
        <Tooltip title="Settings" placement="bottom" overlayClassName="app-topbar-tooltip">
          <div className="settings-icon-wrapper app-topbar__tile" role="button" tabIndex={0} aria-label="Settings">
            <SettingOutlined className="settings-icon" />
          </div>
        </Tooltip>
      </span>
    </Popover>
  );
};

export default SettingsDropdown;
